import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import FAQTitle from "../../components/FAQ/FAQSectionTitle";
import { Accordions } from "../../components/shared/Accordion/Accordions";
import VideoEmbed from "../../components/shared/Video/VideoEmbed";
import {
  HelpPageComponentWrapper,
  HelpPageWrapper,
  HelpGeneralContainer,
  TopicSelectorHelpText,
  HelpTopicSelect,
  HelpTopicSelectMenuItem,
  HelpGeneralContainerSecondary,
} from "./Help.styles";
import { Resources } from "../../components/Resources/Resources";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { GetInTouch } from "./GetInTouch";
import { helpAccordions } from "./HelpData";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { aneuploidyHelpAccordions } from "./AneuploidyHelpData";
import { carrierHelpAccordions } from "./CarrierHelpData";
import { billingHelpAccordion } from "./BillingHelpData";
import BillingVideoEmbed from "../../components/shared/Video/BillingVideoEmbed";
import { UpfrontPaymentHelpWrapper } from "../../components/UpfrontPayment/UpfrontPayment.styles";
import { VideoFeedBackTitleText } from "../../components/shared/Video/VideoEmbed.styles";

import { getFirstRequisitionID } from "../../recoil/selectors";
import { HelpSelectorOption } from "./utils";

const Help = (): React.ReactElement => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const anchor = hash.slice(1);
  const { deviceType } = useDeviceHook();
  const reqID = useRecoilValue(getFirstRequisitionID) ?? "";
  const faqAccordions = React.useMemo(() => helpAccordions(t), [t]);
  const aneuploidyAccordions = React.useMemo(
    () => aneuploidyHelpAccordions(reqID, t),
    [reqID, t],
  );
  const carrierAccordions = React.useMemo(
    () => carrierHelpAccordions(reqID, t),
    [reqID, t],
  );
  const billingAccordions = React.useMemo(() => billingHelpAccordion(t), [t]);
  const helpSelectorOptions = [
    HelpSelectorOption.FAQ,
    HelpSelectorOption.Aneuploidy,
    HelpSelectorOption.Billing,
    HelpSelectorOption.Carrier,
  ];
  const helpSelectorLabels: Record<HelpSelectorOption, string> = {
    [HelpSelectorOption.FAQ]: t("help.options.faq"),
    [HelpSelectorOption.Aneuploidy]: t("help.options.aneuploidy"),
    [HelpSelectorOption.Billing]: t("help.options.billing"),
    [HelpSelectorOption.Carrier]: t("help.options.carrier"),
  };
  const [selectedOptionValue, setSelectedOptionValue] =
    React.useState<HelpSelectorOption>(helpSelectorOptions[0]);
  const hasScrolledRef = React.useRef(false);

  useEffect(() => {
    if (anchor) {
      // Remove '-help' suffix from anchor if present
      const anchorKey = anchor.endsWith("-help")
        ? anchor.replace(/-help$/, "")
        : anchor;

      // Map anchor to HelpSelectorOption based on keys in each accordion data
      if (faqAccordions.some((item) => item.key === anchorKey)) {
        setSelectedOptionValue(HelpSelectorOption.FAQ);
      } else if (aneuploidyAccordions.some((item) => item.key === anchorKey)) {
        setSelectedOptionValue(HelpSelectorOption.Aneuploidy);
      } else if (carrierAccordions.some((item) => item.key === anchorKey)) {
        setSelectedOptionValue(HelpSelectorOption.Carrier);
      } else if (
        billingAccordions.some((item) => item.key === anchorKey) ||
        anchorKey.toLowerCase().includes("billing")
      ) {
        setSelectedOptionValue(HelpSelectorOption.Billing);
      }
    }
  }, [
    anchor,
    aneuploidyAccordions,
    billingAccordions,
    carrierAccordions,
    faqAccordions,
  ]);

  useEffect(() => {
    // Scroll to the anchor element if it exists and hasn't been scrolled to yet
    // This prevents the scroll from happening on every render
    if (!hasScrolledRef.current) {
      const anchorEl = document.getElementById(anchor);
      if (anchorEl) {
        anchorEl.scrollIntoView();
        anchorEl.click();
        hasScrolledRef.current = true;
      }
    }
  }, [selectedOptionValue]);

  return (
    <HelpPageWrapper data-testid="HelpPageWrapper">
      <HelpPageComponentWrapper deviceType={deviceType}>
        {GetTypographyContent({
          content: t("help.title"),
          size: "h2",
          padding: deviceType === "mobile" ? "24px 0 0" : undefined,
          isBold: true,
        })}
        <TopicSelectorHelpText>{t("help.topicSelector")}</TopicSelectorHelpText>
        <HelpTopicSelect
          value={selectedOptionValue}
          onChange={(event) =>
            setSelectedOptionValue(event.target.value as HelpSelectorOption)
          }
          style={{ borderRadius: "24px" }}
          data-testid="help-topic-selector"
        >
          {helpSelectorOptions.map((option) => (
            <HelpTopicSelectMenuItem key={option} value={option}>
              {helpSelectorLabels[option]}
            </HelpTopicSelectMenuItem>
          ))}
        </HelpTopicSelect>
        <HelpGeneralContainer padding="0px" paddingBottom="20px">
          {(() => {
            switch (selectedOptionValue) {
              case HelpSelectorOption.FAQ:
                return (
                  <>
                    <FAQTitle title={t("help.sections.faq")} />
                    <Accordions
                      data={faqAccordions}
                      isLinked={false}
                      size="large"
                    />
                  </>
                );
              case HelpSelectorOption.Aneuploidy:
                return (
                  <>
                    <FAQTitle title={t("help.sections.aneuploidyFaq")} />
                    <Accordions
                      data={aneuploidyAccordions}
                      isLinked={false}
                      size="large"
                    />
                  </>
                );
              case HelpSelectorOption.Carrier:
                return (
                  <>
                    <FAQTitle title={t("help.sections.carrierFaq")} />
                    <Accordions
                      data={carrierAccordions}
                      isLinked={false}
                      size="large"
                    />
                  </>
                );
              case HelpSelectorOption.Billing:
                return (
                  <>
                    <FAQTitle title={t("help.sections.billingFaq")} />
                    <UpfrontPaymentHelpWrapper>
                      <VideoFeedBackTitleText style={{ paddingLeft: "16px" }}>
                        {t("help.billingVideoTitle")}
                      </VideoFeedBackTitleText>
                      <BillingVideoEmbed
                        reqID={reqID}
                        videoLocation="billing-faq-section"
                      />
                    </UpfrontPaymentHelpWrapper>
                    <Accordions
                      data={billingAccordions}
                      isLinked={false}
                      size="large"
                    />
                  </>
                );
              default:
                return null;
            }
          })()}
        </HelpGeneralContainer>
        <HelpGeneralContainer padding="16px">
          <GetInTouch />
        </HelpGeneralContainer>
        <HelpGeneralContainerSecondary padding="16px">
          <VideoEmbed
            overlayText={
              <>
                {t("landing.videoOverlay.prefix")}Unity Complete
                <sup>&reg;</sup>
                {t("landing.videoOverlay.suffix")}
              </>
            }
          />
        </HelpGeneralContainerSecondary>
        <HelpGeneralContainer padding="24px 20px 30px 16px">
          <Resources />
        </HelpGeneralContainer>
      </HelpPageComponentWrapper>
    </HelpPageWrapper>
  );
};

export default Help;
